import React, { useState, useEffect, useCallback } from 'react';
import {
    Activity, AlertTriangle, CheckCircle, Clock, Eye, RefreshCw,
    Server, Users, Zap, ChevronRight, ArrowRight, Info,
    TrendingDown, TrendingUp, Minus, ShieldAlert, ShieldCheck,
    BarChart2, ChevronDown
} from 'lucide-react';
import UserTimelineDrawer from './UserTimelineDrawer';
import { buildReliabilityMetricCards } from '../../utils/lessonReliability';

const API_BASE = '/internal/analytics';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const token = () => localStorage.getItem('auth_token') || localStorage.getItem('saige_admin_token');

const apiFetch = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
};

const relativeTime = (iso) => {
    if (!iso) return '—';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const qualityColor = (quality) => ({
    strong: '#22c55e',
    growing: '#f59e0b',
    partial: '#94a3b8',
}[quality] || '#94a3b8');

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const DataModelBadge = ({ label }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 4,
        background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
        fontSize: 10, fontWeight: 600, color: '#818cf8',
        fontFamily: 'monospace',
    }}>
        <Info size={10} />
        {label}
    </span>
);

const FreshnessLabel = ({ note }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 6,
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
        fontSize: 11, color: '#d97706',
    }}>
        <Clock size={11} />
        {note}
    </div>
);

const MetricPill = ({ label, value, color = '#64748b', sub }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', gap: 2,
        padding: '12px 16px', borderRadius: 8,
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    }}>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 10, color: '#64748b' }}>{sub}</div>}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// OPS STATUS STRIP
// ─────────────────────────────────────────────────────────────────────────────

const OpsStrip = ({ data, loading }) => {
    if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading ops status…</div>;
    if (!data) return null;

    const { deployment_version, pipeline_health_24h } = data;
    const health = pipeline_health_24h || {};
    const successRate = health.success_rate_pct;
    const errorsHour = health.error_events_last_1h ?? 0;

    const healthColor = successRate == null ? '#64748b' : successRate >= 99 ? '#22c55e' : successRate >= 95 ? '#f59e0b' : '#ef4444';

    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
            padding: '12px 16px', borderRadius: 10,
            background: '#0f172a', border: '1px solid #1e293b',
            marginBottom: 24,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
                <Server size={14} color="#64748b" />
                <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                    Deploy:{' '}
                    <span style={{ color: deployment_version ? '#a5f3fc' : '#f59e0b' }}>
                        {deployment_version || 'not set'}
                    </span>
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={14} color={healthColor} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    Pipeline:{' '}
                    <span style={{ color: healthColor, fontWeight: 600 }}>
                        {successRate != null ? `${successRate}%` : 'no data'}
                    </span>
                    {health.note && <span style={{ color: '#475569', marginLeft: 6 }}>({health.note})</span>}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={14} color={errorsHour > 0 ? '#f59e0b' : '#22c55e'} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    Errors (1h):{' '}
                    <span style={{ color: errorsHour > 0 ? '#fb923c' : '#22c55e', fontWeight: 600 }}>
                        {errorsHour}
                    </span>
                </span>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// RECENT ACTIVITY PANEL
// ─────────────────────────────────────────────────────────────────────────────

const RecentActivityPanel = ({ data, loading, onSelectUser }) => {
    if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading activity…</div>;

    const w15 = data?.window_15m || {};
    const w60 = data?.window_60m || {};
    const freshness = data?.freshness_note;

    return (
        <div style={{
            background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Activity size={16} color="#818cf8" />
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>Recent Activity</span>
                </div>
                <DataModelBadge label="data_model: recent_activity" />
            </div>

            {freshness && <FreshnessLabel note={freshness} />}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                <MetricPill
                    label={w15.label || 'Active in last 15 min'}
                    value={w15.unique_users ?? 0}
                    color="#818cf8"
                    sub={`${w15.event_count ?? 0} events`}
                />
                <MetricPill
                    label={w60.label || 'Active in last 60 min'}
                    value={w60.unique_users ?? 0}
                    color="#6366f1"
                    sub={`${w60.event_count ?? 0} events`}
                />
            </div>

            {/* Recent event stream */}
            {(data?.recent_events?.length > 0) && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                        Recent Event Stream
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto' }}>
                        {data.recent_events.map((ev, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '5px 8px', borderRadius: 6,
                                    background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                    cursor: ev.user_uuid ? 'pointer' : 'default',
                                }}
                                onClick={() => ev.user_uuid && onSelectUser && onSelectUser(ev.user_uuid)}
                                title={ev.user_uuid ? `Click to view timeline for ${ev.user_uuid.slice(0,8)}…` : ''}
                            >
                                <span style={{
                                    fontSize: 10, fontFamily: 'monospace', color: '#64748b',
                                    flexShrink: 0, width: 50,
                                }}>
                                    {relativeTime(ev.created_at)}
                                </span>
                                <span style={{
                                    fontSize: 11, fontFamily: 'monospace', color: '#a5b4fc',
                                    flexShrink: 0, minWidth: 0, overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                                }}>
                                    {ev.event_name}
                                </span>
                                {ev.route && (
                                    <span style={{ fontSize: 10, color: '#475569', flexShrink: 0 }}>
                                        {ev.route}
                                    </span>
                                )}
                                {ev.user_uuid && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        flexShrink: 0,
                                    }}>
                                        <Users size={10} color="#4f46e5" />
                                        <span style={{ fontSize: 10, color: '#4f46e5' }}>
                                            {ev.user_uuid.slice(0, 8)}…
                                        </span>
                                        <ChevronRight size={10} color="#374151" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(!data?.recent_events?.length) && !loading && (
                <div style={{ marginTop: 16, color: '#475569', fontSize: 12, textAlign: 'center' }}>
                    No events in the last 60 minutes. Events appear here as telemetry fires.
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// LESSON JOURNEY FUNNEL
// ─────────────────────────────────────────────────────────────────────────────

const LessonJourneyPanel = ({ data, loading }) => {
    if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading journey…</div>;
    if (!data) return null;

    const steps = data.steps || [];
    const baseline = data.baseline_lessons || 0;
    const maxCount = Math.max(...steps.map(s => s.count), 1);

    return (
        <div style={{
            background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ArrowRight size={16} color="#34d399" />
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>Lesson Journey</span>
                </div>
                <DataModelBadge label="data_model: hybrid_backfill" />
            </div>

            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 16 }}>
                {data.data_model_note}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {steps.map((step, idx) => {
                    const barWidth = maxCount > 0 ? Math.max((step.count / maxCount) * 100, step.count > 0 ? 4 : 0) : 0;
                    const color = qualityColor(step.data_quality);
                    return (
                        <div key={step.step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 20, height: 20, borderRadius: '50%',
                                background: '#1e293b', border: '1px solid #334155',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, color: '#64748b', flexShrink: 0,
                            }}>
                                {idx + 1}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, color: '#cbd5e1' }}>{step.label}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {step.conversion_from_generated_pct != null && (
                                            <span style={{ fontSize: 10, color: '#64748b' }}>
                                                {step.conversion_from_generated_pct}%
                                            </span>
                                        )}
                                        <span style={{ fontSize: 12, fontWeight: 600, color: step.count > 0 ? color : '#374151' }}>
                                            {step.count}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', width: `${barWidth}%`,
                                        background: color,
                                        borderRadius: 2,
                                        transition: 'width 0.4s ease',
                                    }} />
                                </div>
                                <div style={{ fontSize: 9, color: '#475569', marginTop: 2, display: 'flex', gap: 6 }}>
                                    <span style={{ fontFamily: 'monospace' }}>{step.data_model}</span>
                                    <span style={{ color: qualityColor(step.data_quality) }}>● {step.data_quality}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {baseline === 0 && (
                <div style={{
                    marginTop: 16, padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                    fontSize: 11, color: '#d97706',
                }}>
                    No lessons in this period — journey chain will populate when teachers generate lessons.
                </div>
            )}
        </div>
    );
};

const LessonReliabilityPanel = ({ data, loading }) => {
    if (loading && !data) {
        return (
            <div style={{
                background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20,
                marginBottom: 20, color: '#64748b', fontSize: 13,
            }}>
                Loading lesson reliability…
            </div>
        );
    }

    const cards = buildReliabilityMetricCards(data);

    return (
        <div
            style={{
                background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20,
                marginBottom: 20,
            }}
            data-testid="lesson-reliability-panel"
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BarChart2 size={16} color="#f59e0b" />
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>
                        Lesson Reliability
                    </span>
                </div>
                <span style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>
                    {data?.period_days || 7}d window
                </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                {cards.map((card) => (
                    <div
                        key={card.key}
                        style={{
                            padding: '12px 14px',
                            borderRadius: 10,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}
                        data-testid={`reliability-card-${card.key}`}
                    >
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{card.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#f8fafc', marginTop: 4 }}>
                            {card.value}
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                            {card.detail}
                        </div>
                    </div>
                ))}
            </div>

            {data?.note && (
                <div style={{ fontSize: 10, color: '#475569', marginTop: 12 }}>
                    {data.note}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// WATCH ITEMS PANEL
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
    critical: {
        color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)',
        label: 'Critical', icon: ShieldAlert,
    },
    warning: {
        color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)',
        label: 'Warning', icon: AlertTriangle,
    },
    info: {
        color: '#60a5fa', bg: 'rgba(96,165,250,0.06)', border: 'rgba(96,165,250,0.2)',
        label: 'Info', icon: Info,
    },
};

const DirectionIcon = ({ direction }) => {
    if (direction === 'down') return <TrendingDown size={12} color="#ef4444" />;
    if (direction === 'up') return <TrendingUp size={12} color="#f59e0b" />;
    return <Minus size={12} color="#64748b" />;
};

const WatchItemCard = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const cfg = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.info;
    const Icon = cfg.icon;

    return (
        <div style={{
            borderRadius: 8, border: `1px solid ${cfg.border}`,
            background: cfg.bg, overflow: 'hidden',
        }}>
            {/* Header row */}
            <div
                style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 14px', cursor: 'pointer',
                }}
                onClick={() => setExpanded(e => !e)}
            >
                <Icon size={14} color={cfg.color} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9' }}>
                            {item.title}
                        </span>
                        <span style={{
                            fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: '0.06em', color: cfg.color,
                            padding: '1px 6px', borderRadius: 3,
                            border: `1px solid ${cfg.border}`,
                        }}>
                            {cfg.label}
                        </span>
                        <DirectionIcon direction={item.direction} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, lineHeight: 1.5 }}>
                        {item.body}
                    </div>
                </div>
                <div style={{
                    fontSize: 10, color: '#475569', flexShrink: 0,
                    fontFamily: 'monospace', marginLeft: 8,
                }}>
                    {expanded ? '▲' : '▼'}
                </div>
            </div>

            {/* Expanded: trust metadata */}
            {expanded && (
                <div style={{
                    padding: '8px 14px 12px 14px',
                    borderTop: `1px solid ${cfg.border}`,
                    background: 'rgba(0,0,0,0.15)',
                }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                        <div style={{ fontSize: 10.5, color: '#64748b' }}>
                            <span style={{ color: '#475569', fontWeight: 600 }}>Window:</span>{' '}
                            {item.current_window}
                            {item.comparison_window && (
                                <span> vs {item.comparison_window}</span>
                            )}
                        </div>
                        {item.current_value != null && (
                            <div style={{ fontSize: 10.5, color: '#64748b' }}>
                                <span style={{ color: '#475569', fontWeight: 600 }}>Current:</span>{' '}
                                {item.current_value}{' '}
                                {item.prior_value != null && (
                                    <span>vs prior {item.prior_value}</span>
                                )}
                            </div>
                        )}
                        {item.deployment_version && (
                            <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#5b21b6' }}>
                                deploy: {item.deployment_version}
                            </div>
                        )}
                        {item.feature_area && (
                            <div style={{ fontSize: 10, color: '#475569' }}>
                                area: {item.feature_area}
                            </div>
                        )}
                    </div>
                    <div style={{ fontSize: 10, color: '#475569', marginBottom: 6, fontFamily: 'monospace' }}>
                        rule: {item.rule}
                    </div>
                    <div style={{ fontSize: 10, color: '#475569', marginBottom: 6, fontFamily: 'monospace' }}>
                        source: {item.data_source} · confidence: {item.confidence}
                    </div>
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 6,
                        padding: '6px 10px', borderRadius: 6,
                        background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                        fontSize: 11, color: '#818cf8',
                    }}>
                        <Eye size={11} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>{item.investigate_hint}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const WatchItemsPanel = ({ data, loading }) => {
    const items = data?.items || [];
    const hasCritical = items.some(i => i.severity === 'critical');

    if (loading && !data) {
        return (
            <div style={{
                padding: '12px 16px', borderRadius: 10,
                background: '#0f172a', border: '1px solid #1e293b',
                color: '#475569', fontSize: 12, marginBottom: 20,
            }}>
                Checking watch items…
            </div>
        );
    }

    if (!data) return null;

    // All-clear state
    if (items.length === 0) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', borderRadius: 10,
                background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)',
                marginBottom: 20,
            }}>
                <ShieldCheck size={15} color="#22c55e" />
                <span style={{ fontSize: 12, color: '#4ade80' }}>All clear — no watch items fired</span>
                <span style={{ fontSize: 10, color: '#374151', marginLeft: 'auto' }}>
                    {data.total === 0 ? `${data.period_days}d window · ${new Date(data.queried_at).toLocaleTimeString()}` : ''}
                </span>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 24 }}>
            {/* Panel header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <ShieldAlert size={16} color={hasCritical ? '#ef4444' : '#f59e0b'} />
                <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>
                    Watch Items
                </span>
                <span style={{
                    fontSize: 11, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 10,
                    background: hasCritical ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.12)',
                    color: hasCritical ? '#fca5a5' : '#fcd34d',
                    border: `1px solid ${hasCritical ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.25)'}`,
                }}>
                    {items.length} item{items.length !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 10, color: '#374151', marginLeft: 'auto', fontFamily: 'monospace' }}>
                    deterministic rules · {data.period_days}d window
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(item => (
                    <WatchItemCard key={item.id} item={item} />
                ))}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SESSION QUALITY PANEL
// ─────────────────────────────────────────────────────────────────────────────

const SessionQualityPanel = ({ data, loading, onSelectUser }) => {
    const [expandedTier, setExpandedTier] = useState(null);

    if (loading && !data) {
        return (
            <div style={{
                padding: '16px 20px', borderRadius: 12,
                background: '#0f172a', border: '1px solid #1e293b',
                color: '#475569', fontSize: 12, marginTop: 24,
            }}>
                Analyzing session quality…
            </div>
        );
    }

    if (!data || data.total_sessions === 0) {
        return (
            <div style={{
                padding: '16px 20px', borderRadius: 12,
                background: '#0f172a', border: '1px solid #1e293b',
                marginTop: 24,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <BarChart2 size={16} color="#475569" />
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>Session Quality</span>
                </div>
                <div style={{ fontSize: 12, color: '#475569' }}>
                    No session data available yet. Quality analysis requires workspace_events with session_id.
                </div>
            </div>
        );
    }

    const distribution = data.distribution || [];
    const breakpoints = data.breakpoints || [];
    const trend = data.trend || [];
    const examples = data.examples || {};
    const maxCount = Math.max(...distribution.map(d => d.count), 1);

    return (
        <div style={{
            borderRadius: 12, border: '1px solid #1e293b',
            background: '#0f172a', padding: '20px 24px', marginTop: 24,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BarChart2 size={18} color="#818cf8" />
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>Session Quality</span>
                    <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
                        border: '1px solid rgba(99,102,241,0.2)',
                    }}>
                        {data.total_sessions} session{data.total_sessions !== 1 ? 's' : ''}
                    </span>
                </div>
                <div style={{ fontSize: 10, color: '#374151', fontFamily: 'monospace' }}>
                    deterministic · {data.period_days}d window · {data.confidence}
                </div>
            </div>

            {/* Distribution Bars */}
            <div style={{ marginBottom: 24 }}>
                {distribution.map(d => (
                    <div
                        key={d.tier}
                        style={{ marginBottom: 10, cursor: 'pointer' }}
                        onClick={() => setExpandedTier(expandedTier === d.tier ? null : d.tier)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: d.color, display: 'inline-block',
                                }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{d.label}</span>
                                <span style={{ fontSize: 10, color: '#475569' }}>{d.description}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: d.color }}>{d.count}</span>
                                <span style={{ fontSize: 10, color: '#64748b' }}>{d.pct}%</span>
                                <ChevronDown
                                    size={12}
                                    color="#475569"
                                    style={{
                                        transform: expandedTier === d.tier ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.15s',
                                    }}
                                />
                            </div>
                        </div>
                        {/* Bar */}
                        <div style={{
                            width: '100%', height: 6, borderRadius: 3,
                            background: 'rgba(255,255,255,0.04)',
                        }}>
                            <div style={{
                                width: `${Math.max((d.count / maxCount) * 100, d.count > 0 ? 2 : 0)}%`,
                                height: '100%', borderRadius: 3,
                                background: d.color, opacity: 0.7,
                                transition: 'width 0.3s ease',
                            }} />
                        </div>

                        {/* Expanded: example sessions */}
                        {expandedTier === d.tier && (examples[d.tier] || []).length > 0 && (
                            <div style={{
                                marginTop: 8, padding: '8px 12px', borderRadius: 8,
                                background: 'rgba(255,255,255,0.02)',
                                border: '1px solid rgba(255,255,255,0.05)',
                            }}>
                                <div style={{ fontSize: 10, color: '#475569', marginBottom: 6, fontWeight: 600 }}>
                                    Recent {d.label} sessions:
                                </div>
                                {(examples[d.tier] || []).map((s, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '4px 0', borderBottom: i < (examples[d.tier].length - 1) ? '1px solid rgba(255,255,255,0.03)' : 'none',
                                    }}>
                                        <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#5b21b6', minWidth: 70 }}>
                                            {s.session_id ? s.session_id.slice(0, 10) + '…' : '—'}
                                        </span>
                                        <span style={{ fontSize: 10, color: '#64748b' }}>
                                            {s.event_count} events
                                        </span>
                                        <span style={{ fontSize: 10, color: '#334155', flex: 1 }}>
                                            {(s.events_reached || []).join(' → ')}
                                        </span>
                                        {s.user_uuid && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onSelectUser(s.user_uuid); }}
                                                style={{
                                                    fontSize: 9, color: '#818cf8', background: 'none',
                                                    border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4,
                                                    padding: '1px 6px', cursor: 'pointer',
                                                }}
                                            >
                                                timeline →
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Two columns: Breakpoints + Trend */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Breakpoints */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 10 }}>
                        Quality Breakpoints
                    </div>
                    <div style={{ fontSize: 10, color: '#475569', marginBottom: 8 }}>
                        Where partial / shallow / abandoned sessions most often stop:
                    </div>
                    {breakpoints.length === 0 ? (
                        <div style={{ fontSize: 11, color: '#334155' }}>No breakpoint data yet.</div>
                    ) : (
                        breakpoints.slice(0, 6).map((bp, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '5px 0',
                                borderBottom: i < Math.min(breakpoints.length, 6) - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                            }}>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                                    {bp.milestone.replace(/_/g, ' ')}
                                </span>
                                <span style={{
                                    fontSize: 12, fontWeight: 700,
                                    color: bp.sessions_stopped_here >= 3 ? '#f59e0b' : '#64748b',
                                }}>
                                    {bp.sessions_stopped_here}
                                </span>
                            </div>
                        ))
                    )}
                </div>

                {/* Trend */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 10 }}>
                        Quality Trend (vs prior {data.period_days}d)
                    </div>
                    <div style={{ fontSize: 10, color: '#475569', marginBottom: 8 }}>
                        {data.total_sessions} sessions current vs {data.prior_total_sessions} prior
                    </div>
                    {trend.map((t, i) => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '4px 0',
                            borderBottom: i < trend.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                        }}>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>{t.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                    {t.current_pct}%
                                </span>
                                {t.direction === 'up' && <TrendingUp size={11} color="#22c55e" />}
                                {t.direction === 'down' && <TrendingDown size={11} color="#ef4444" />}
                                {t.direction === 'stable' && <Minus size={11} color="#475569" />}
                                <span style={{
                                    fontSize: 10, fontFamily: 'monospace',
                                    color: t.delta_pct > 0 ? '#4ade80' : t.delta_pct < 0 ? '#fca5a5' : '#475569',
                                }}>
                                    {t.delta_pct > 0 ? '+' : ''}{t.delta_pct}pp
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Trust footer */}
            <div style={{
                marginTop: 16, paddingTop: 12,
                borderTop: '1px solid rgba(255,255,255,0.04)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 10, color: '#334155', fontFamily: 'monospace',
            }}>
                <Info size={10} />
                <span>
                    source: {data.data_source} · confidence: {data.confidence}
                    {data.data_model_note && ` · note: ${data.data_model_note.slice(0, 80)}…`}
                </span>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// LIVE OPS TAB
// ─────────────────────────────────────────────────────────────────────────────

const LiveOpsTab = ({ filters = {} }) => {
    const days = filters.days || 7;

    const [opsData, setOpsData] = useState(null);
    const [activityData, setActivityData] = useState(null);
    const [journeyData, setJourneyData] = useState(null);
    const [watchData, setWatchData] = useState(null);
    const [qualityData, setQualityData] = useState(null);
    const [reliabilityData, setReliabilityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    const [selectedUser, setSelectedUser] = useState(null);
    const [timelineOpen, setTimelineOpen] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [ops, activity, journey, watch, quality, reliability] = await Promise.allSettled([
                apiFetch('/ops-summary'),
                apiFetch('/live-activity'),
                apiFetch(`/lesson-journey?days=${days}`),
                apiFetch(`/watch-items?days=${days}`),
                apiFetch(`/session-quality?days=${days}`),
                apiFetch(`/lesson-reliability?days=${days}`),
            ]);

            if (ops.status === 'fulfilled') setOpsData(ops.value);
            if (activity.status === 'fulfilled') setActivityData(activity.value);
            if (journey.status === 'fulfilled') setJourneyData(journey.value);
            if (watch.status === 'fulfilled') setWatchData(watch.value);
            if (quality.status === 'fulfilled') setQualityData(quality.value);
            if (reliability.status === 'fulfilled') setReliabilityData(reliability.value);

            setLastRefresh(new Date());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        loadAll();
        // Auto-refresh every 60 seconds
        const interval = setInterval(loadAll, 60_000);
        return () => clearInterval(interval);
    }, [loadAll]);

    const handleSelectUser = (userUuid) => {
        setSelectedUser(userUuid);
        setTimelineOpen(true);
    };

    return (
        <div style={{ maxWidth: 960 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Zap size={20} color="#818cf8" />
                        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
                            Live Ops
                        </h1>
                    </div>
                    <p style={{ color: '#64748b', fontSize: 13, margin: '6px 0 0' }}>
                        Deployment-aware ops intelligence + lesson journey monitoring.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {lastRefresh && (
                        <span style={{ fontSize: 11, color: '#475569' }}>
                            Refreshed {relativeTime(lastRefresh.toISOString())} · auto-refreshes every 60s
                        </span>
                    )}
                    <button
                        id="live-ops-refresh-btn"
                        onClick={loadAll}
                        disabled={loading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px', borderRadius: 8,
                            border: '1px solid #334155', background: '#1e293b',
                            color: '#94a3b8', fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    padding: '12px 16px', borderRadius: 8,
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    color: '#fca5a5', fontSize: 13, marginBottom: 16,
                }}>
                    {error}
                </div>
            )}

            {/* Ops Status Strip */}
            <OpsStrip data={opsData} loading={loading && !opsData} />

            <LessonReliabilityPanel
                data={reliabilityData}
                loading={loading && !reliabilityData}
            />

            {/* Watch Items Panel */}
            <WatchItemsPanel
                data={watchData}
                loading={loading && !watchData}
            />

            {/* Two-column layout: Activity + Journey */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                <RecentActivityPanel
                    data={activityData}
                    loading={loading && !activityData}
                    onSelectUser={handleSelectUser}
                />
                <LessonJourneyPanel
                    data={journeyData}
                    loading={loading && !journeyData}
                />
            </div>

            {/* Session Quality Panel */}
            <SessionQualityPanel
                data={qualityData}
                loading={loading && !qualityData}
                onSelectUser={handleSelectUser}
            />

            {/* CSS for spin animation */}
            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>

            {/* User Timeline Drawer */}
            <UserTimelineDrawer
                userUuid={selectedUser}
                open={timelineOpen}
                onClose={() => setTimelineOpen(false)}
                days={days}
            />
        </div>
    );
};

export default LiveOpsTab;
